// Environment configuration for DP Portal
interface ApiEndpoints {
    portal: {
        validateToken: string;
        sendOtp: string;
        validateOtp: string;
        getForms: string;
        getFormDetails: string;
    };
    auth: {
        verifyToken: string;
        sendOtp: string;
        verifyOtp: string;
        logout: string;
    };
    identifiers: {
        search: string;
        link: string;
        unlink: string;
    };
    data: {
        getDataPrincipal: string;
        getConsents: string;
        getDataElements: string;
        updateData: string;
        requestErasure: string;
    };
    purposes: {
        getPurposes: string;
        withdrawConsent: string;
    };
    notices: {
        getNotices: string;
        respondToNotice: string;
    };
    requests: {
        getRequests: string;
        createRequest: string;
    };
}

interface EnvironmentConfig {
    isProduction: boolean;
    domain: string;
    enableLogging: boolean;
    api: {
        baseUrl: string;
        endpoints: ApiEndpoints;
    };
    forms: {
        baseUrl: string;
    };
    auth: {
        maxOtpAttempts: number;
    };
    ui: {
        snackbar: {
            autoHideDuration: number;
            anchorOrigin: {
                vertical: 'top' | 'bottom';
                horizontal: 'left' | 'center' | 'right';
            };
        };
    };
}

// Development configuration
const devConfig: EnvironmentConfig = {
    isProduction: false,
    domain: 'localhost',
    enableLogging: true,
    api: {
        baseUrl: process.env.REACT_APP_DEV_API_BASE_URL || 'http://localhost:3001',
        endpoints: {
            portal: {
                validateToken: '/portal/validate-token',
                sendOtp: '/portal/send-otp',
                validateOtp: '/portal/validate-otp',
                getForms: '/portal/forms',
                getFormDetails: '/portal/forms'
            },
            auth: {
                verifyToken: '/auth/verify-token',
                sendOtp: '/auth/send-otp',
                verifyOtp: '/auth/verify-otp',
                logout: '/auth/logout'
            },
            identifiers: {
                search: '/identifiers/search',
                link: '/identifiers/link-account',
                unlink: '/identifiers/unlink'
            },
            data: {
                getDataPrincipal: '/data-principals/me',
                getConsents: '/consents',
                getDataElements: '/data-elements',
                updateData: '/data-elements/update',
                requestErasure: '/data-elements/request-erasure'
            },
            purposes: {
                getPurposes: '/purposes',
                withdrawConsent: '/purposes/withdraw-consent'
            },
            notices: {
                getNotices: '/notices',
                respondToNotice: '/notices/respond'
            },
            requests: {
                getRequests: '/requests',
                createRequest: '/requests'
            }
        }
    },
    forms: {
        baseUrl: process.env.REACT_APP_FORMS_BASE_URL || 'http://localhost:5501'
    },
    auth: {
        maxOtpAttempts: 3
    },
    ui: {
        snackbar: {
            autoHideDuration: 5000, // 5 seconds
            anchorOrigin: {
                vertical: 'top' as const,
                horizontal: 'center' as const
            }
        }
    }
};

// Production configuration
const prodConfig: EnvironmentConfig = {
    isProduction: true,
    domain: 'portal.privigo.ai',
    enableLogging: false,
    api: {
        // Default to Privigo dev API unless explicitly overridden at build/deploy time.
        // If production API cutover is not complete, set REACT_APP_PROD_API_BASE_URL accordingly.
        baseUrl: process.env.REACT_APP_PROD_API_BASE_URL || 'https://apidev.privigo.ai',
        endpoints: {
            portal: {
                validateToken: '/portal/validate-token',
                sendOtp: '/portal/send-otp',
                validateOtp: '/portal/validate-otp',
                getForms: '/portal/forms',
                getFormDetails: '/portal/forms'
            },
            auth: {
                verifyToken: '/auth/verify-token',
                sendOtp: '/auth/send-otp',
                verifyOtp: '/auth/verify-otp',
                logout: '/auth/logout'
            },
            identifiers: {
                search: '/identifiers/search',
                link: '/identifiers/link-account',
                unlink: '/identifiers/unlink'
            },
            data: {
                getDataPrincipal: '/data-principals/me',
                getConsents: '/consents',
                getDataElements: '/data-elements',
                updateData: '/data-elements/update',
                requestErasure: '/data-elements/request-erasure'
            },
            purposes: {
                getPurposes: '/purposes',
                withdrawConsent: '/purposes/withdraw-consent'
            },
            notices: {
                getNotices: '/notices',
                respondToNotice: '/notices/respond'
            },
            requests: {
                getRequests: '/requests',
                createRequest: '/requests'
            }
        }
    },
    forms: {
        baseUrl: process.env.REACT_APP_FORMS_BASE_URL || 'https://forms.privigo.ai'
    },
    auth: {
        maxOtpAttempts: 3
    },
    ui: {
        snackbar: {
            autoHideDuration: 5000, // 5 seconds
            anchorOrigin: {
                vertical: 'top' as const,
                horizontal: 'center' as const
            }
        }
    }
};

// Export the appropriate configuration based on environment
console.log('NODE_ENV:', process.env.NODE_ENV);
console.log('Config:', process.env.NODE_ENV === 'production' ? prodConfig : devConfig);
export const config = process.env.NODE_ENV === 'production' ? prodConfig : devConfig;
